import { Button, Dropdown, Input, showAlert } from '@/components';
import { useAccountForm } from '@/context/accountFormContext/AccountFormContextProvider';
import React, { useEffect, useState } from 'react';
import { getValidationError } from './accountFormValidation';
import SalesRep from './SalesRep';
import JSONVIEW from '@/components/ui/JSONVIEW';

const AccountForm = ({ closeModal, formData }) => {
  const {
    accountForm,
    setAccountForm,
    errors,
    setErrors,
    getAllStatesAPI,
    createAccountForm,
    checkProntoCodeUnique,
    isProntoUnique,
    updateAccountForm,
    clearFormData,
    saveAndSendAccountForm,
    updateAndSendMailAccountForm,
  } = useAccountForm();
  const [isFormValid, setIsFormValid] = useState(false);
  const [states, setStates] = useState([]);

  const handleClose = () => {
    clearFormData();
    closeModal();
  };

  useEffect(() => {
    if (formData) {
      setAccountForm(formData);
    }
  }, [formData]);

  const BooleanFields = [
    'isItBranchAccount',
    'nationalAccount',
    'chocolatesReqFirstOrder',
    'catalogeReqFirstOrder',
    'addNotes',
    'contactPersonForAccountsPayable',
    'quotesToAddToContract',
    'onlineAccountRequired',
    'additionalOnlineUserRequired',
  ];

  useEffect(() => {
    getAllStatesAPI().then((data) => {
      setStates(data);
    });
    return () => {
      setStates([]);
    };
  }, []);

  const statesOptions = states?.map((state) => ({
    label: state.state,
    value: String(state.id), // Ensure it's a string if Dropdown passes value as string
    data: state,
  }));

  const handleStateSelect = (e) => {
    const { name, value } = e.target;

    const selectedState = statesOptions.find(
      (option) => option.value === value,
    );
    if (name === 'address.stateId') {
      if (selectedState) {
        setAccountForm((prev) => ({
          ...prev,
          address: {
            ...prev.address,
            stateId: selectedState.data,
          },
        }));
        validateInput(name, value, 'text');
      } else {
        showAlert('error', 'State not found');
      }
    }
    if (name === 'address.deliveryStateId') {
      if (selectedState) {
        setAccountForm((prev) => ({
          ...prev,
          address: {
            ...prev.address,
            deliveryStateId: selectedState.data,
          },
        }));
        validateInput(name, value, 'text');
      } else {
        showAlert('error', 'State not found');
      }
    }
  };

  const validateInput = (name, value, type) => {
    const error = getValidationError(value, type);
    if (name.startsWith('address.')) {
      const addressField = name.split('.')[1];
      setErrors((prev) => ({
        ...prev,
        address: { ...prev.address, [addressField]: error },
      }));
    } else {
      setErrors((prev) => ({ ...prev, [name]: error }));
    }
  };

  useEffect(() => {
    if (accountForm.nationalAccount === true) {
      setAccountForm((prev) => ({ ...prev, warehouse: 'NAT' }));
    } else {
      setAccountForm((prev) => ({ ...prev, warehouse: 'JAY' }));
    }
  }, [accountForm.nationalAccount]);

  useEffect(() => {
    if (
      accountForm.customerType === 'E3' ||
      accountForm.customerType === 'E4'
    ) {
      setAccountForm((prev) => ({ ...prev, priceLevel: 'LEVEL 0' }));
    }
  }, [accountForm.customerType]);

  useEffect(() => {
    if (accountForm.deliverySameAsBilling === true) {
      setAccountForm((prev) => ({
        ...prev,
        address: {
          ...prev.address,
          deliveryAddLine1: prev.address.addressLane1,
          deliveryAddLine2: prev.address.addressLane2,
          deliverySuburb: prev.address.suburb,
          deliveryStateId: prev.address.stateId,
          deliveryPostalCode: prev.address.postalCode,
        },
      }));
    }
  }, [
    accountForm.deliverySameAsBilling,
    accountForm.address.addressLane1,
    accountForm.address.addressLane2,
    accountForm.address.suburb,
    accountForm.address.stateId,
    accountForm.address.postalCode,
  ]);

  useEffect(() => {
    if (accountForm.isItBranchAccount === false) {
      setAccountForm((prev) => ({ ...prev, businessCode: '' }));
    }
    if (accountForm.isItBranchAccount === true) {
      setAccountForm((prev) => ({ ...prev, marketingFlag: '' }));
    }
  }, [accountForm.isItBranchAccount]);

  const fieldsToValidate = [
    'accountType',
    'prontoAccountCode',
    'isItBranchAccount',
    'company',
    'nationalAccount',
    'warehouse',
    'customerType',
    'priceLevel',
    'territory',
    'address.addressLane1',
    'address.suburb',
    'address.postalCode',
    'phoneNumber',
    'emailAddress',
    'companyABN',
    'fullContactName',
    'industry',
    'rankOfTheCustomer',
    'address.deliveryAddLine1',
    'address.deliverySuburb',
    'address.deliveryPostalCode',
    'invoiceType',
    'emailAddressForInvoice',
    'emailAddressForStatements',
    'noOfStaff',
    'monthlySpend',
    'deliveryType',
    'billToHO',
  ];

  const validateForm = () => {
    let formIsValid = true;
    let newErrors = {};

    const validateField = (condition, field, message) => {
      if (condition) {
        newErrors[field] = message;
        formIsValid = false;
      } else {
        newErrors[field] = '';
      }
    };

    // Validate boolean fields
    BooleanFields.forEach((field) => {
      if (field in accountForm) {
        const value = accountForm[field];
        validateField(
          value === null || value === undefined,
          field,
          'This field is required.',
        );
      }
    });

    // Validate salesPerson
    validateField(
      accountForm?.salesPerson?.id === 0 ||
        accountForm?.salesPerson?.id === null ||
        accountForm?.salesPerson?.id === undefined,
      'salesPerson',
      'Please select a Sales Rep',
    );

    // Validate deliveryStateId
    validateField(
      accountForm.address?.deliveryStateId?.id === 0 ||
        accountForm.address?.deliveryStateId?.id === null ||
        accountForm.address?.deliveryStateId?.id === undefined,
      'deliveryStateId',
      'Please select a State',
    );

    // Validate businessCode
    if (accountForm.isItBranchAccount === true) {
      validateField(
        accountForm.businessCode === '' ||
          accountForm.businessCode !== accountForm.businessCode.trim(),
        'businessCode',
        'This field is required.',
      );
    }

    // Validate marketingFlag
    if (accountForm.isItBranchAccount === false) {
      validateField(
        accountForm.marketingFlag === '',
        'marketingFlag',
        'This field is required.',
      );
    }

    // Validate contactPersonForAccountsPayable
    if (accountForm.contactPersonForAccountsPayable === false) {
      [
        'accountPayableContactName',
        'accountPayableContactNo',
        'accountPayableEmail',
      ].forEach((field) => {
        validateField(
          accountForm[field] === '',
          field,
          'This field is required.',
        );
      });
    }

    // Validate stateId
    validateField(
      accountForm.address?.stateId?.id === 0 ||
        accountForm.address?.stateId?.id === null ||
        accountForm.address?.stateId?.id === undefined,
      'stateId',
      'Please select a State',
    );

    // Validation for Quote to add to contract
    if (accountForm.quotesToAddToContract === true) {
      validateField(
        accountForm.salesOrderNo === '',
        'salesOrderNo',
        'This field is required.',
      );
    }

    // Validation for Online Account Required
    if (accountForm.onlineAccountRequired === true) {
      ['addQuotesToFavourites', 'approvalSystemRequired'].forEach((field) => {
        validateField(
          accountForm[field] === null ||
            accountForm[field] === undefined ||
            accountForm[field] === '',
          field,
          'This field is required.',
        );
      });
    }

    if (accountForm.approvalSystemRequired === true) {
      ['approvalLimit', 'approversFullName', 'approversEmail'].forEach(
        (field) => {
          validateField(
            accountForm[field] === '' ||
              accountForm[field] === null ||
              accountForm[field] === undefined,
            field,
            'This field is required.',
          );
        },
      );
    }

    // Validation for Additional Online User Required
    if (accountForm.additionalOnlineUserRequired === true) {
      ['additionalUserName', 'additionalUserEmail'].forEach((field) => {
        validateField(
          accountForm[field] === '' ||
            accountForm[field] === null ||
            accountForm[field] === undefined,
          field,
          'This field is required.',
        );
      });
    }

    // Validate noOfStaff and monthlySpend
    validateField(
      accountForm.noOfStaff === null || accountForm.noOfStaff === undefined,
      'noOfStaff',
      'This field is required.',
    );

    validateField(
      accountForm.monthlySpend === null ||
        accountForm.monthlySpend === undefined,
      'monthlySpend',
      'This field is required.',
    );

    validateField(
      accountForm.phoneNumber.length !== 12,
      'phoneNumber',
      'Phone Number should be 10 digits',
    );
    // Validation for Bill To HO Account Code
    if (accountForm.billToHO === true) {
      validateField(
        !accountForm.billToHOAccountCode,
        'billToHOAccountCode',
        'Bill To HO Account Code is required.',
      );
    }
    // Validate other fields
    fieldsToValidate.forEach((field) => {
      const keys = field.split('.');
      let value = accountForm;

      keys.forEach((key) => {
        if (value && key in value) {
          value = value[key];
        } else {
          value = undefined;
        }
      });

      const error = getValidationError(value, 'text');
      if (error) {
        formIsValid = false;
        if (field.startsWith('address.')) {
          const addressField = field.split('.')[1];
          newErrors = {
            ...newErrors,
            address: { ...newErrors.address, [addressField]: error },
          };
        } else {
          newErrors = { ...newErrors, [field]: error };
        }
      }
    });

    // Remove fields with empty values from the errors object
    const cleanedErrors = Object.keys(newErrors).reduce((acc, key) => {
      if (typeof newErrors[key] === 'object') {
        const nestedErrors = Object.keys(newErrors[key]).reduce(
          (nestedAcc, nestedKey) => {
            if (newErrors[key][nestedKey]) {
              nestedAcc[nestedKey] = newErrors[key][nestedKey];
            }
            return nestedAcc;
          },
          {},
        );

        if (Object.keys(nestedErrors).length > 0) {
          acc[key] = nestedErrors;
        }
      } else if (newErrors[key]) {
        acc[key] = newErrors[key];
      }
      return acc;
    }, {});

    setErrors(cleanedErrors);
    setIsFormValid(formIsValid);
    return formIsValid;
  };

  // Reset errors when form values change
  useEffect(() => {
    if (accountForm.additionalOnlineUserRequired === false) {
      setAccountForm((prev) => ({ ...prev, additionalUserName: '' }));
      setAccountForm((prev) => ({ ...prev, additionalUserEmail: '' }));
    }
  }, [accountForm.additionalOnlineUserRequired]);

  useEffect(() => {
    if (accountForm.onlineAccountRequired === false) {
      setAccountForm((prev) => ({ ...prev, addQuotesToFavourites: false }));
      setAccountForm((prev) => ({ ...prev, approvalSystemRequired: false }));
    }
  }, [accountForm.onlineAccountRequired]);
  useEffect(() => {
    if (accountForm.approvalSystemRequired === false) {
      setAccountForm((prev) => ({ ...prev, approvalLimit: '' }));
      setAccountForm((prev) => ({ ...prev, approversFullName: '' }));
      setAccountForm((prev) => ({ ...prev, approversEmail: '' }));
    }
  }, [accountForm.approvalSystemRequired]);
  // Reset errors when form values change

  // Reset errors when form values change
  useEffect(() => {
    if (accountForm.quotesToAddToContract === false) {
      setAccountForm((prev) => ({ ...prev, salesOrderNo: '' }));
    }
  }, [accountForm.quotesToAddToContract]);

  // Reset errors when form values change
  useEffect(() => {
    if (accountForm.isItBranchAccount === false) {
      setAccountForm((prev) => ({ ...prev, businessCode: '' }));
    } else if (accountForm.isItBranchAccount === true) {
      setAccountForm((prev) => ({ ...prev, marketingFlag: '' }));
    }
  }, [accountForm.isItBranchAccount]);

  // Reset errors when form values change
  useEffect(() => {
    if (accountForm.contactPersonForAccountsPayable === true) {
      setAccountForm((prev) => ({
        ...prev,
        accountPayableContactName: accountForm.fullContactName,
      }));
      setAccountForm((prev) => ({
        ...prev,
        accountPayableContactNo: accountForm.phoneNumber,
      }));
      setAccountForm((prev) => ({
        ...prev,
        accountPayableEmail: accountForm.emailAddress,
      }));
    }
  }, [accountForm.contactPersonForAccountsPayable]);

  const sendMail = async (event) => {
    event.preventDefault();
    if (validateForm()) {
      if (accountForm.id > 0) {
        updateAndSendMailAccountForm(accountForm, accountForm.id, closeModal);
      } else {
        await saveAndSendAccountForm(accountForm, closeModal);
      }
    } else {
      showAlert('error', 'Please fill all the required fields');
    }
  };

  const handleSubmit = async (event) => {
    event.preventDefault();
    if (validateForm()) {
      if (accountForm.id > 0) {
        updateAccountForm(accountForm, accountForm.id, closeModal);
      } else {
        await createAccountForm(accountForm, closeModal);
      }

      // Submit form logic here
    } else {
      showAlert('error', 'Please fill all the required fields');
    }
  };

  const handleHandleDropdownBlur = (e) => {
    const { name, value } = e.target;
    if (value === '' || value === null) {
      setErrors((prev) => ({ ...prev, [name]: 'This field is required.' }));
    } else {
      setErrors((prev) => ({ ...prev, [name]: '' }));
    }
  };

  const formatPhoneNumber = (value) => {
    const cleaned = ('' + value).replace(/\D/g, '').slice(0, 10); // Allow only 10 digits
    const match = cleaned.match(/^(\d{2})(\d{4})(\d{4})$/);
    if (match) {
      return `${match[1]} ${match[2]} ${match[3]}`;
    }
    return cleaned;
  };

  const formatCompanyABN = (value) => {
    const cleaned = ('' + value).replace(/\D/g, '').slice(0, 12); // Allow only 11 digits
    const match = cleaned.match(/^(\d{2})(\d{3})(\d{3})(\d{4})$/);
    if (match) {
      return `${match[1]} ${match[2]} ${match[3]} ${match[4]}`;
    }
    return cleaned;
  };

  const handleChange = (event) => {
    const { name, value, selectionStart, selectionEnd } = event.target;

    // Capture cursor position before re-render
    const cursorStart = selectionStart;
    const cursorEnd = selectionEnd;

    // Always convert to uppercase
    let formattedValue = value.toUpperCase();

    // Apply field-specific formatting
    if (name === 'phoneNumber' || name === 'accountPayableContactNo') {
      formattedValue = formatPhoneNumber(formattedValue);
    } else if (name === 'companyABN') {
      formattedValue = formatCompanyABN(formattedValue);
    }

    const keys = name.split('.');

    setAccountForm((prevState) => {
      // Shallow clone only along the path being updated
      const updatedForm = { ...prevState };
      let currentLevel = updatedForm;

      for (let i = 0; i < keys.length - 1; i++) {
        currentLevel[keys[i]] = { ...currentLevel[keys[i]] };
        currentLevel = currentLevel[keys[i]];
      }

      currentLevel[keys[keys.length - 1]] = formattedValue;

      return updatedForm;
    });

    // Restore cursor position immediately after DOM update
    requestAnimationFrame(() => {
      if (event.target && event.target.setSelectionRange) {
        event.target.setSelectionRange(cursorStart, cursorEnd);
      }
    });
  };

  const handleKeyPress = (event) => {
    const charCode = event.charCode;
    if (charCode < 48 || charCode > 57) {
      event.preventDefault();
    }
  };

  const handleDropdownChange = (e) => {
    const { name, value } = e.target;
    const parsedValue =
      value === 'true' ? true : value === 'false' ? false : value;
    setAccountForm((prev) => ({ ...prev, [name]: parsedValue }));
    if (name === 'billToHO' && parsedValue === false) {
      setAccountForm((prev) => ({
        ...prev,
        [name]: parsedValue,
        billToHOAccountCode: '',
      }));
    } else {
      setAccountForm((prev) => ({ ...prev, [name]: parsedValue }));
    }
  };

  const handleBlur = (e) => {
    const { name, value } = e.target;
    const type = e.target.type === 'text' ? 'text' : e.target.type;
    validateInput(name, value, type);
  };

  const handleRadioChange = (e) => {
    const { name, value } = e.target;

    setAccountForm((prev) => {
      const updatedForm = {
        ...prev,
        [name]: value === 'true', // Convert to boolean
      };
      return updatedForm;
    });
  };

  const handleCheckboxChange = (e) => {
    const { name, checked } = e.target;
    setAccountForm((prev) => ({ ...prev, [name]: checked }));
  };
  return (
    <>
      <form className="font-400" onSubmit={handleSubmit}>
        <div className="grid grid-cols-2 gap-5 items-center ">
          <Dropdown
            onBlur={handleHandleDropdownBlur}
            label="ACCOUNT TYPE"
            name="accountType"
            options={[
              { label: 'NEW ACCOUNT', value: 'NEW ACCOUNT' },
              { label: 'LAPSED ACCOUNT', value: 'LAPSED ACCOUNT' },
            ]}
            onChange={handleChange}
            value={accountForm.accountType}
            required
            error={errors.accountType}
          />
          <Input
            type="text"
            name="prontoAccountCode"
            label="PRONTO ACCOUNT CODE"
            placeholder="ENTER PRONTO ACCOUNT CODE"
            value={accountForm.prontoAccountCode}
            onChange={handleChange}
            onBlur={checkProntoCodeUnique}
            error={errors.prontoAccountCode}
            disabled={accountForm.id > 0}
            errType={isProntoUnique ? 'valid' : ''}
            required
          />
          <Dropdown
            onBlur={handleHandleDropdownBlur}
            label="IS IT A BRANCH ACCOUNT"
            name="isItBranchAccount"
            options={[
              { label: 'YES', value: true },
              { label: 'NO', value: false },
            ]}
            onChange={handleDropdownChange}
            value={accountForm.isItBranchAccount}
            required
            error={errors.isItBranchAccount}
          />
          {accountForm.isItBranchAccount === true && (
            <Input
              type="text"
              name="businessCode"
              label="BUSINESS CODE"
              placeholder="ENTER BUSINESS CODE"
              value={accountForm.businessCode}
              onChange={handleChange}
              onBlur={handleBlur}
              error={errors.businessCode}
              required
            />
          )}
          <Input
            type="text"
            name="company"
            label="COMPANY"
            placeholder="ENTER COMPANY"
            value={accountForm.company}
            onBlur={handleBlur}
            onChange={handleChange}
            error={errors.company}
            required
          />
          <Dropdown
            onBlur={handleHandleDropdownBlur}
            name="nationalAccount"
            label="IS IT A NATIONAL ACCOUNT?"
            options={[
              { label: 'YES', value: true },
              { label: 'NO', value: false },
            ]}
            onChange={handleDropdownChange}
            value={accountForm.nationalAccount}
            required
            error={errors.nationalAccount}
          />
          <Dropdown
            onBlur={handleHandleDropdownBlur}
            name="warehouse"
            disabled
            label="WAREHOUSE"
            options={[
              { label: 'JAY', value: 'JAY' },
              { label: 'NAT', value: 'NAT' },
            ]}
            value={accountForm.warehouse}
            required
            error={errors.warehouse}
          />
          <Dropdown
            onBlur={handleHandleDropdownBlur}
            name="customerType"
            label="CUSTOMER TYPE"
            options={[
              { label: 'DEFAULT', value: 'DEFAULT' },
              { label: 'E3', value: 'E3' },
              { label: 'E4', value: 'E4' },
            ]}
            onChange={handleChange}
            value={accountForm.customerType}
            required
            error={errors.customerType}
          />
          <Dropdown
            onBlur={handleHandleDropdownBlur}
            name="priceLevel"
            label="PRICE LEVEL"
            options={[
              { label: 'LEVEL 0', value: 'LEVEL 0' },
              { label: 'LEVEL 1', value: 'LEVEL 1' },
              { label: 'LEVEL 2', value: 'LEVEL 2' },
              { label: 'LEVEL 3', value: 'LEVEL 3' },
              { label: 'LEVEL 4', value: 'LEVEL 4' },
            ]}
            onChange={handleChange}
            value={
              accountForm.customerType == 'E3' ||
              accountForm.customerType == 'E4'
                ? 'LEVEL 0'
                : accountForm.priceLevel
            }
            required
            error={errors.priceLevel}
            disabled={
              accountForm.customerType === 'E3' ||
              accountForm.customerType === 'E4'
            }
          />
          <SalesRep />
          <Dropdown
            onBlur={handleHandleDropdownBlur}
            name="territory"
            label="TERRITORY"
            options={[
              { label: 'JAY', value: 'JAY' },
              { label: 'NAT', value: 'NAT' },
            ]}
            onChange={handleChange}
            value={accountForm.territory}
            required
            error={errors.territory}
          />
          <Input
            type="text"
            name="address.addressLane1"
            label="ADDRESS LINE 1"
            placeholder="ENTER ADDRESS LINE 1"
            value={accountForm.address.addressLane1}
            onChange={handleChange}
            onBlur={handleBlur}
            error={errors.address?.addressLane1}
            required
          />
          <Input
            name="address.addressLane2"
            label="ADDRESS LINE 2"
            placeholder="ENTER ADDRESS LINE 2"
            value={accountForm.address?.addressLane2}
            onChange={handleChange}
          />
          <Input
            type="text"
            name="address.suburb"
            label="SUBURB"
            placeholder="ENTER SUBURB"
            value={accountForm.address?.suburb}
            onChange={handleChange}
            onBlur={handleBlur}
            error={errors.address?.suburb}
            required
          />
          <Dropdown
            onBlur={handleHandleDropdownBlur}
            name="address.stateId"
            label="STATE"
            options={statesOptions}
            onChange={handleStateSelect}
            value={accountForm.address?.stateId?.id || ''} // Fallback to empty string
            required
            error={
              accountForm.address.stateId.id == 0
                ? errors.address?.stateId || errors?.stateId
                : ''
            }
            disabled={!statesOptions.length} // Disable Dropdown until options are loaded
          />
          <Input
            type="number"
            name="address.postalCode"
            label="POSTAL CODE"
            onBlur={handleBlur}
            placeholder="ENTER POSTAL CODE"
            value={accountForm.address?.postalCode}
            onChange={handleChange}
            error={errors.address?.postalCode}
            required
          />
          <Input
            name="phoneNumber"
            type="text"
            onKeyPress={handleKeyPress}
            inputMode="numeric"
            max={12}
            label="PHONE NUMBER"
            placeholder="ENTER PHONE NUMBER"
            value={accountForm.phoneNumber}
            onChange={handleChange}
            error={errors.phoneNumber}
            onBlur={handleBlur}
            required
          />
          <Input
            type="email"
            name="emailAddress"
            label="EMAIL ADDRESS"
            placeholder="ENTER EMAIL ADDRESS"
            value={accountForm.emailAddress}
            onChange={handleChange}
            onBlur={handleBlur}
            error={errors.emailAddress}
            required
          />
          <Input
            name="companyABN"
            type="text"
            label="COMPANY ABN"
            placeholder="ENTER COMPANY ABN"
            value={accountForm.companyABN}
            onChange={handleChange}
            onKeyPress={handleKeyPress}
            inputMode="numeric"
            error={errors.companyABN}
            onBlur={handleBlur}
            maxLength={15}
            required
          />
          <Input
            type="text"
            name="fullContactName"
            label="FULL CONTACT NAME"
            placeholder="ENTER FULL CONTACT NAME"
            value={accountForm.fullContactName}
            onChange={handleChange}
            onBlur={handleBlur}
            error={errors.fullContactName}
            required
          />
          <Dropdown
            onBlur={handleHandleDropdownBlur}
            name="industry"
            label="INDUSTRY"
            options={[
              { label: 'N-NORTH', value: 'N-NORTH' },
              { label: 'S-SOUTH', value: 'S-SOUTH' },
              { label: 'E-EAST', value: 'E-EAST' },
              { label: 'W-WEST', value: 'W-WEST' },
            ]}
            onChange={handleChange}
            value={accountForm.industry}
            required
            error={errors.industry}
          />
          <Dropdown
            onBlur={handleHandleDropdownBlur}
            name="marketingFlag"
            label="MARKETING FLAG"
            options={[
              { label: '35', value: '35' },
              { label: '36', value: '36' },
              { label: '37', value: '37' },
              { label: '39', value: '39' },
              { label: '40', value: '40' },
            ]}
            onChange={handleChange}
            value={accountForm.marketingFlag}
            disabled={accountForm.isItBranchAccount === true}
            required={accountForm.isItBranchAccount === false}
            error={errors.marketingFlag}
          />
          <Dropdown
            onBlur={handleHandleDropdownBlur}
            name="deliveryType"
            label="DELIVERY TYPE"
            options={[
              { label: 'STANDARD', value: 'STANDARD' },
              { label: 'LOCAL DELIVERY', value: 'LOCAL DELIVERY' },
              {
                label: 'STATIONERY REFILL SERVICE',
                value: 'STATIONERY REFILL SERVICE',
              },
              { label: 'NATIONAL ACCOUNT', value: 'NATIONAL ACCOUNTS' },
            ]}
            onChange={handleChange}
            value={accountForm.deliveryType}
            error={errors.deliveryType}
            required
          />
          <Dropdown
            onBlur={handleHandleDropdownBlur}
            name="rankOfTheCustomer"
            label="CHOOSE RANK OF THE CUSTOMER"
            options={[
              { label: 'RANK A', value: 'RANK A' },
              { label: 'RANK B', value: 'RANK B' },
              { label: 'RANK C', value: 'RANK C' },
              { label: 'RANK D', value: 'RANK D' },
              { label: 'RANK E', value: 'RANK E' },
            ]}
            onChange={handleChange}
            value={accountForm.rankOfTheCustomer}
            required
            error={errors.rankOfTheCustomer}
          />
          <div className="m-2 gap-5">
            <label>IS DELIVERY ADDRESS SAME AS BILLING ADDRESS?</label>
            <div className="">
              <input
                className="mx-5"
                type="radio"
                name="deliverySameAsBilling"
                value="true"
                checked={accountForm.deliverySameAsBilling === true}
                onChange={handleRadioChange}
              />
              Yes
              <input
                className="mx-5"
                type="radio"
                name="deliverySameAsBilling"
                value="false"
                checked={accountForm.deliverySameAsBilling === false}
                onChange={handleRadioChange}
              />
              No
            </div>
          </div>
          <Dropdown
            onBlur={handleHandleDropdownBlur}
            name="billToHO"
            label="BILL TO H/O"
            options={[
              { label: 'YES', value: 'true' },
              { label: 'NO', value: 'false' },
            ]}
            onChange={handleDropdownChange}
            value={accountForm.billToHO}
            required
            error={errors.billToHO}
          />
          {accountForm.billToHO === true && (
            <Input
              name="billToHOAccountCode"
              label="ACCOUNT CODE"
              placeholder="Enter Account Code"
              value={accountForm.billToHOAccountCode || ''}
              onChange={handleChange}
              onBlur={handleBlur}
              required
              error={errors.billToHOAccountCode}
            />
          )}
          <Input
            type="text"
            name="address.deliveryAddLine1"
            label="DELIVERY ADDRESS LINE 1"
            placeholder="ENTER DELIVERY ADDRESS LINE 1"
            value={accountForm.address.deliveryAddLine1}
            onChange={handleChange}
            required
            onBlur={handleBlur}
            error={errors.address?.deliveryAddLine1}
            disabled={accountForm.deliverySameAsBilling}
          />
          <Input
            type="text"
            name="address.deliveryAddLine2"
            label="DELIVERY ADDRESS LINE 2"
            placeholder="ENTER DELIVERY ADDRESS LINE 2"
            value={accountForm.address.deliveryAddLine2}
            onChange={handleChange}
            disabled={accountForm.deliverySameAsBilling}
          />
          <Input
            type="text"
            name="address.deliverySuburb"
            label="DELIVERY SUBURB"
            placeholder="ENTER DELIVERY SUBURB"
            value={accountForm.address.deliverySuburb}
            onChange={handleChange}
            required
            onBlur={handleBlur}
            error={errors.address?.deliverySuburb}
            disabled={accountForm.deliverySameAsBilling}
          />
          <Dropdown
            onBlur={handleHandleDropdownBlur}
            name="address.deliveryStateId"
            label="DELIVERY STATE"
            options={statesOptions}
            onChange={handleStateSelect}
            required
            value={accountForm?.address?.deliveryStateId?.id || ''}
            error={
              accountForm.address.deliveryStateId.id == 0 ||
              accountForm.address.deliveryStateId.id == '' ||
              accountForm.address.deliveryStateId.id == null
                ? errors.address?.deliveryStateId || errors?.deliveryStateId
                : ''
            }
            disabled={
              accountForm.deliverySameAsBilling || !statesOptions.length
            }
          />
          <Input
            type="number"
            name="address.deliveryPostalCode"
            label="DELIVERY POSTAL CODE"
            placeholder="ENTER DELIVERY POSTAL CODE"
            value={accountForm.address.deliveryPostalCode}
            onChange={handleChange}
            required
            onBlur={handleBlur}
            error={errors.address?.deliveryPostalCode}
            disabled={accountForm.deliverySameAsBilling}
          />
          <Input
            type="textarea"
            name="deliveryInstruction"
            label="DELIVERY INSTRUCTION"
            placeholder="ENTER DELIVERY INSTRUCTION"
            value={accountForm.deliveryInstruction}
            onChange={handleChange}
          />
          <Dropdown
            onBlur={handleHandleDropdownBlur}
            name="chocolatesReqFirstOrder"
            label="CHOCOLATES REQUIRED ON FIRST ORDER"
            options={[
              { label: 'YES', value: true },
              { label: 'NO', value: false },
            ]}
            onChange={handleDropdownChange}
            required
            value={accountForm.chocolatesReqFirstOrder}
            error={errors.chocolatesReqFirstOrder}
          />
          <Dropdown
            onBlur={handleHandleDropdownBlur}
            name="catalogeReqFirstOrder"
            label="CATALOGUE REQUIRED ON FIRST ORDER"
            options={[
              { label: 'YES', value: true },
              { label: 'NO', value: false },
            ]}
            onChange={handleDropdownChange}
            required
            value={accountForm.catalogeReqFirstOrder}
            error={errors.catalogeReqFirstOrder}
          />
          <Input
            name="custNotePad"
            type="textarea"
            label="WARNING NOTES (CUST NOTEPAD)"
            placeholder="ENTER WARNING NOTES"
            value={accountForm.custNotePad}
            onChange={handleChange}
          />
          <Dropdown
            onBlur={handleHandleDropdownBlur}
            name="addNotes"
            label="ADD NOTES"
            options={[
              { label: 'YES', value: true },
              { label: 'NO', value: false },
            ]}
            onChange={handleDropdownChange}
            error={errors.addNotes}
            value={accountForm.addNotes}
            required
          />
          <Dropdown
            onBlur={handleHandleDropdownBlur}
            name="invoiceType"
            label="INVOICE TYPE"
            options={[
              { label: 'STANDARD INVOICE', value: 'STANDARD INVOICE' },
              {
                label: '1 INVOICE PER ORDER',
                value: '1 INVOICE PER ORDER',
              },
              {
                label: '1 INVOICE PER MONTH',
                value: '1 INVOICE PER MONTH',
              },
              {
                label: '1 INVOICE PER MONTH PER BRANCH',
                value: '1 INVOICE PER MONTH PERBRANCH',
              },
            ]}
            onChange={handleDropdownChange}
            required
            value={accountForm.invoiceType}
            error={errors.invoiceType}
          />
          <Dropdown
            onBlur={handleHandleDropdownBlur}
            name="contactPersonForAccountsPayable"
            label="CONTACT PERSON FOR ACCOUNTS PAYABLE IS SAME AS ABOVE?"
            options={[
              { label: 'YES', value: true },
              { label: 'NO', value: false },
            ]}
            onChange={handleDropdownChange}
            value={accountForm.contactPersonForAccountsPayable}
            required
            error={errors.contactPersonForAccountsPayable}
          />
          {accountForm.contactPersonForAccountsPayable === false && (
            <>
              <Input
                name="accountPayableContactName"
                label="ACCOUNT PAYABLE CONTACT NAME"
                placeholder="ENTER ACCOUNT PAYABLE CONTACT NAME"
                value={accountForm.accountPayableContactName}
                onChange={handleChange}
                error={errors.accountPayableContactName}
                onBlur={handleBlur}
                required
              />
              <Input
                name="accountPayableContactNo"
                type="text"
                onKeyPress={handleKeyPress}
                inputMode="numeric"
                max={12}
                label="ACCOUNT PAYABLE CONTACT NUMBER"
                placeholder="ENTER ACCOUNT PAYABLE CONTACT NUMBER"
                value={accountForm.accountPayableContactNo}
                onChange={handleChange}
                error={errors.accountPayableContactNo}
                onBlur={handleBlur}
                required
              />
              <Input
                type="email"
                name="accountPayableEmail"
                label="ACCOUNT PAYABLE EMAIL"
                placeholder="ENTER ACCOUNT PAYABLE EMAIL"
                value={accountForm.accountPayableEmail}
                onChange={handleChange}
                error={errors.accountPayableEmail}
                onBlur={handleBlur}
                required
              />
            </>
          )}
          <Input
            type="email"
            name="emailAddressForInvoice"
            label="EMAIL ADDRESS FOR INVOICE"
            placeholder="ENTER EMAIL ADDRESS FOR INVOICE"
            value={accountForm.emailAddressForInvoice}
            onChange={handleChange}
            onBlur={handleBlur}
            error={errors.emailAddressForInvoice}
            required
          />
          <Input
            type="email"
            name="emailAddressForStatements"
            label="EMAIL ADDRESS FOR STATEMENTS"
            placeholder="ENTER EMAIL ADDRESS FOR STATEMENTS"
            value={accountForm.emailAddressForStatements}
            onChange={handleChange}
            onBlur={handleBlur}
            error={errors.emailAddressForStatements}
            required
          />
          <div>
            <label className="block text-gray-700  mb-1 m-1">CONTRACT</label>
            <div className="">
              <Input
                type="checkbox"
                name="extensivePricelist"
                label="555666 - EXTENSIVE PRICELIST"
                value={accountForm.extensivePricelist}
                checked={accountForm.extensivePricelist === true}
                onChange={handleCheckboxChange}
              />
              <Input
                type="checkbox"
                name="eductionPricelist"
                label="555600 - EDUCATION PRICELIST"
                value={accountForm.eductionPricelist}
                checked={accountForm.eductionPricelist === true}
                onChange={handleCheckboxChange}
              />

              <Input
                type="checkbox"
                name="kitchenJanitorialPriceList"
                label="555777- KITCHEN & JANITORIAL PRICE LIST"
                value={accountForm.kitchenJanitorialPriceList}
                checked={accountForm.kitchenJanitorialPriceList === true}
                onChange={handleCheckboxChange}
              />
            </div>
          </div>
          <Dropdown
            onBlur={handleHandleDropdownBlur}
            name="quotesToAddToContract"
            label="QUOTES TO ADD TO CONTRACT"
            options={[
              { label: 'YES', value: true },
              { label: 'NO', value: false },
            ]}
            onChange={handleDropdownChange}
            value={accountForm.quotesToAddToContract}
            required
            error={errors.quotesToAddToContract}
          />
          {accountForm.quotesToAddToContract === true && (
            <Input
              type="text"
              name="salesOrderNo"
              label="QUOTE NO / SALES ORDER NO"
              placeholder="ENTER QUOTE NO / SALES ORDER NO"
              onChange={handleChange}
              onBlur={handleBlur}
              required
              value={accountForm.salesOrderNo}
              error={errors.salesOrderNo}
            />
          )}
          <Dropdown
            onBlur={handleHandleDropdownBlur}
            name="onlineAccountRequired"
            label="ONLINE ACCOUNT REQUIRED"
            options={[
              { label: 'YES', value: true },
              { label: 'NO', value: false },
            ]}
            onChange={handleDropdownChange}
            required
            value={accountForm.onlineAccountRequired}
            error={errors.onlineAccountRequired}
          />
          {accountForm.onlineAccountRequired === true && (
            <>
              <Dropdown
                onBlur={handleHandleDropdownBlur}
                name="addQuotesToFavourites"
                label="ADD QUOTES TO FAVOURITES"
                options={[
                  { label: 'YES', value: true },
                  { label: 'NO', value: false },
                ]}
                onChange={handleDropdownChange}
                value={accountForm.addQuotesToFavourites}
                required
                error={errors.addQuotesToFavourites}
              />
              <Dropdown
                onBlur={handleHandleDropdownBlur}
                name="approvalSystemRequired"
                label="APPROVAL SYSTEM REQUIRED"
                options={[
                  { label: 'YES', value: true },
                  { label: 'NO', value: false },
                ]}
                value={accountForm.approvalSystemRequired}
                onChange={handleDropdownChange}
                required
                error={errors.approvalSystemRequired}
              />
            </>
          )}
          {accountForm.approvalSystemRequired === true && (
            <>
              <Input
                type="number"
                name="approvalLimit"
                label="APPROVAL LIMIT"
                required
                value={accountForm.approvalLimit}
                onChange={handleChange}
                onBlur={handleBlur}
                error={errors.approvalLimit}
              />
              <Input
                type="text"
                name="approversFullName"
                label="APPROVER'S FULL NAME"
                required
                value={accountForm.approversFullName}
                onChange={handleChange}
                onBlur={handleBlur}
                error={errors.approversFullName}
              />
              <Input
                type="email"
                name="approversEmail"
                label="APPROVER'S EMAIL ADDRESS"
                required
                value={accountForm.approversEmail}
                onChange={handleChange}
                onBlur={handleBlur}
                error={errors.approversEmail}
              />
            </>
          )}
          <Dropdown
            onBlur={handleHandleDropdownBlur}
            name="additionalOnlineUserRequired"
            label="ADDITIONAL ONLINE USER REQUIRED?"
            options={[
              { label: 'YES', value: true },
              { label: 'NO', value: false },
            ]}
            onChange={handleDropdownChange}
            value={accountForm.additionalOnlineUserRequired}
            error={errors.additionalOnlineUserRequired}
            required
          />
          {accountForm.additionalOnlineUserRequired === true && (
            <>
              <Input
                type="text"
                name="additionalUserName"
                label="ADDITIONAL USER FULL NAME"
                required
                value={accountForm.additionalUserName}
                onChange={handleChange}
                onBlur={handleBlur}
                error={errors.additionalUserName}
              />
              <Input
                type="email"
                name="additionalUserEmail"
                label="ADDITIONAL USER EMAIL ADDRESS"
                required
                value={accountForm.additionalUserEmail}
                onChange={handleChange}
                onBlur={handleBlur}
                error={errors.additionalUserEmail}
              />
            </>
          )}
          <Input
            name="noOfStaff"
            type="number"
            label="NO OF STAFF"
            required
            value={accountForm.noOfStaff}
            onChange={handleChange}
            onBlur={handleBlur}
            error={errors.noOfStaff}
          />
          <Input
            name="monthlySpend"
            label="MONTHLY SPEND"
            required
            value={accountForm.monthlySpend}
            onBlur={handleBlur}
            onChange={handleChange}
            type="number"
            error={errors.monthlySpend}
          />
          <Input
            name="comments"
            type="textarea"
            label="COMMENTS"
            placeholder="ENTER COMMENTS"
            value={accountForm.comments}
            onChange={handleChange}
          />
          )
        </div>
        {/* I wanna print json of accountForm */}
        <div className="flex justify-center gap-5">
          <Button type={'submit'}>
            {accountForm.id > 0 ? 'Update' : 'Save'}
          </Button>
          <Button type={'button'} onClick={sendMail}>
            {accountForm.id > 0 ? 'Update & Send Mail' : 'Save & Send Mail'}
          </Button>
          <Button onClick={handleClose}>Cancel</Button>
        </div>
      </form>
      <JSONVIEW>
        <pre>{JSON.stringify(accountForm, null, 2)}</pre>
      </JSONVIEW>
    </>
  );
};

export default AccountForm;
